import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import {
  LayoutDashboard, CheckSquare, FileText, Briefcase, CalendarDays,
  Target, GraduationCap, BookOpen, Settings, User, Shield, Plus,
  Search, Clock, ArrowRight, Zap, Sparkles,
} from 'lucide-react';
import { useTasks } from '@/hooks/useTasks';
import { useMeetings } from '@/hooks/useMeetings';
import { useJobApplications } from '@/hooks/useJobApplications';
import { useIsAdmin } from '@/hooks/useAdmin';
import { format, parseISO, isAfter } from 'date-fns';
import { cn } from '@/lib/utils';

const pages = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', keywords: 'home overview stats', color: 'bg-primary/10 text-primary' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks', keywords: 'todo list kanban', color: 'bg-primary/10 text-primary' },
  { icon: FileText, label: 'Work Log', path: '/worklog', keywords: 'time tracking hours', color: 'bg-info/10 text-info' },
  { icon: Briefcase, label: 'Applications', path: '/applications', keywords: 'jobs pipeline hiring', color: 'bg-warning/10 text-warning' },
  { icon: CalendarDays, label: 'Calendar', path: '/calendar', keywords: 'schedule dates events', color: 'bg-success/10 text-success' },
  { icon: Target, label: 'Meetings', path: '/meetings', keywords: 'calls agenda', color: 'bg-success/10 text-success' },
  { icon: GraduationCap, label: 'Skills', path: '/skills', keywords: 'learning upskilling progress', color: 'bg-purple-500/10 text-purple-500' },
  { icon: BookOpen, label: 'Diary', path: '/diary', keywords: 'journal notes daily', color: 'bg-info/10 text-info' },
  { icon: User, label: 'Profile', path: '/profile', keywords: 'account avatar name', color: 'bg-muted text-muted-foreground' },
  { icon: Settings, label: 'Settings', path: '/settings', keywords: 'preferences theme', color: 'bg-muted text-muted-foreground' },
];

const quickActions = [
  { icon: CheckSquare, label: 'New Task', path: '/tasks', action: 'new-task', keywords: 'create add task', color: 'bg-primary/10 text-primary' },
  { icon: FileText, label: 'Log Work', path: '/worklog', action: 'new-worklog', keywords: 'create add work log time', color: 'bg-info/10 text-info' },
  { icon: Target, label: 'New Meeting', path: '/meetings', action: 'new-meeting', keywords: 'create add meeting schedule', color: 'bg-success/10 text-success' },
  { icon: Briefcase, label: 'New Application', path: '/applications', action: 'new-application', keywords: 'create add job apply', color: 'bg-warning/10 text-warning' },
  { icon: BookOpen, label: 'New Diary Entry', path: '/diary', action: 'new-diary', keywords: 'create add journal write', color: 'bg-info/10 text-info' },
  { icon: GraduationCap, label: 'Add Skill', path: '/skills', action: 'new-skill', keywords: 'create add learning', color: 'bg-purple-500/10 text-purple-500' },
];

const priorityIndicator: Record<string, string> = {
  high: 'bg-destructive',
  medium: 'bg-warning',
  low: 'bg-success',
};

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { data: tasks = [] } = useTasks();
  const { data: meetings = [] } = useMeetings();
  const { data: applications = [] } = useJobApplications();
  const { data: isAdmin } = useIsAdmin();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const runCommand = useCallback((callback: () => void) => {
    onOpenChange(false);
    requestAnimationFrame(callback);
  }, [onOpenChange]);

  const activeTasks = useMemo(() =>
    tasks.filter((t) => t.status !== 'done' && t.status !== 'archived').slice(0, 6),
    [tasks]
  );

  const upcomingMeetings = useMemo(() =>
    meetings.filter((m) => isAfter(parseISO(m.date_time), new Date()) && m.status !== 'cancelled').slice(0, 4),
    [meetings]
  );

  const recentApplications = useMemo(() =>
    applications.filter((a) => a.status !== 'rejected' && a.status !== 'accepted').slice(0, 4),
    [applications]
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search pages, tasks, meetings, or type a command…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-3 py-10">
            <div className="rounded-2xl bg-muted/50 p-4">
              <Search className="h-8 w-8 text-muted-foreground/30" />
            </div>
            <div className="text-center">
              <p className="font-medium text-muted-foreground">No results found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Try a different search term</p>
            </div>
          </div>
        </CommandEmpty>

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          {quickActions.map((action) => (
            <CommandItem
              key={action.action}
              value={`action ${action.label} ${action.keywords}`}
              onSelect={() => runCommand(() => navigate(action.path, { state: { action: action.action } }))}
              className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
            >
              <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg shrink-0', action.color)}>
                <Plus className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium">{action.label}</span>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground/40">
                <Zap className="h-3 w-3" />
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator className="my-1" />

        {/* Navigate */}
        <CommandGroup heading="Navigate">
          {pages.map((page) => (
            <CommandItem
              key={page.path}
              value={`page ${page.label} ${page.keywords}`}
              onSelect={() => runCommand(() => navigate(page.path))}
              className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
            >
              <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg shrink-0', page.color)}>
                <page.icon className="h-4 w-4" />
              </div>
              <span className="text-sm">{page.label}</span>
              <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground/30" />
            </CommandItem>
          ))}
          {isAdmin && (
            <CommandItem
              value="page Admin Panel admin dashboard management"
              onSelect={() => runCommand(() => navigate('/admin'))}
              className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                <Shield className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm font-medium text-primary">Admin Panel</span>
              <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground/30" />
            </CommandItem>
          )}
        </CommandGroup>

        {/* Active Tasks */}
        {activeTasks.length > 0 && (
          <>
            <CommandSeparator className="my-1" />
            <CommandGroup heading="Active Tasks">
              {activeTasks.map((task) => (
                <CommandItem
                  key={task.id}
                  value={`task ${task.title} ${task.category} ${task.priority || ''}`}
                  onSelect={() => runCommand(() => navigate('/tasks', { state: { editTaskId: task.id } }))}
                  className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
                >
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={cn('h-2.5 w-2.5 rounded-full ring-2 ring-background', priorityIndicator[task.priority || 'medium'])} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{task.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {task.category}{task.due_date ? ` · Due ${format(parseISO(task.due_date), 'MMM d')}` : ''}
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Upcoming Meetings */}
        {upcomingMeetings.length > 0 && (
          <>
            <CommandSeparator className="my-1" />
            <CommandGroup heading="Upcoming Meetings">
              {upcomingMeetings.map((meeting) => (
                <CommandItem
                  key={meeting.id}
                  value={`meeting ${meeting.title} ${meeting.meeting_platform || ''}`}
                  onSelect={() => runCommand(() => navigate('/meetings', { state: { editMeetingId: meeting.id } }))}
                  className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-success/10 shrink-0">
                    <Target className="h-3.5 w-3.5 text-success" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{meeting.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {format(parseISO(meeting.date_time), 'MMM d, h:mm a')} · {meeting.duration}min
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Active Applications */}
        {recentApplications.length > 0 && (
          <>
            <CommandSeparator className="my-1" />
            <CommandGroup heading="Applications">
              {recentApplications.map((app) => (
                <CommandItem
                  key={app.id}
                  value={`application ${app.position} ${app.company} ${app.status || ''}`}
                  onSelect={() => runCommand(() => navigate('/applications', { state: { editAppId: app.id } }))}
                  className="gap-3 rounded-xl py-2.5 mx-1 mb-0.5"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning/10 shrink-0">
                    <Briefcase className="h-3.5 w-3.5 text-warning" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{app.position}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{app.company} · {app.status}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>

      {/* Footer */}
      <div className="border-t border-border/40 px-4 py-2.5 flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-md bg-background border border-border/60 px-1.5 py-0.5 font-mono text-[10px] shadow-sm">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-md bg-background border border-border/60 px-1.5 py-0.5 font-mono text-[10px] shadow-sm">↵</kbd>
            Open
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-md bg-background border border-border/60 px-1.5 py-0.5 font-mono text-[10px] shadow-sm">Esc</kbd>
            Close
          </span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/50">
          <Sparkles className="h-3 w-3" />
          Velora
        </div>
      </div>
    </CommandDialog>
  );
}
